/**
 * maintenance.ts — the workshop records this contract's gate clears on.
 *
 * THE READ IS BY CONTRACT, NOT BY VEHICLE, AND THAT IS THE CORRECTION. The sheet this replaces
 * read `vehicle_maintenances` by `vehicle_id`, ordered by start date, `.limit(1)` — one row, the
 * newest, for a vehicle that may have been through the workshop for three different contracts.
 * `get_preparation_status` counts rows against the CONTRACT, so the list the admin edits and the
 * list the gate reads were two different lists. This one is the gate's.
 *
 * THE WRITE IS THE SHEET'S, MOVED: same columns, same `contract_id` stamped on every row, same
 * insert/update split. `type` is now carried by the caller instead of being hard-coded to
 * `servicing` — a conversion is the other member the gate counts, and the sheet could only ever
 * record it as a service.
 *
 * THE READ IS ALSO FILTERED TO THE TWO TYPES THE GATE COUNTS, and that filter is not cosmetic.
 * `maintenance_type` carries six members; `get_preparation_status` clears its gate on `servicing`
 * or `conversion` alone, and the block that draws this list offers exactly those two in its type
 * picker. An unfiltered read therefore lists a warranty or a refurbishment job under "Service and
 * conversion", draws it with a `QuietSelect` whose value is in none of its options — a blank
 * control — and reclassifies the row as `servicing` the moment anybody saves it. The sheet this
 * replaces filtered the same way; the contract scope is the correction, not the type scope.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

import { SERVICE_TYPES, validateServiceDraft, type ServiceDraft } from './serviceDraft';

export const contractMaintenanceKey = (contractId: number) =>
    ['vehicle-maintenance', 'contract', contractId] as const;

export interface ContractMaintenance {
    id: number;
    type: string | null;
    description: string | null;
    started_at: string | null;
    ended_at: string | null;
    transaction_id: number | null;
    amount: number | null;
}

interface RawMaintenanceRow {
    id: number;
    type: string | null;
    description: string | null;
    started_at: string | null;
    ended_at: string | null;
    transaction_id: number | null;
    transactions: { amount: number | null } | { amount: number | null }[] | null;
}

/** This contract's workshop records, oldest first — the order the block stacks them in. */
export async function fetchContractMaintenance(
    contractId: number,
): Promise<ContractMaintenance[]> {
    if (!contractId) return [];
    const { data, error } = await supabase
        .from('vehicle_maintenances')
        .select(`
            id,
            type,
            description,
            started_at,
            ended_at,
            transaction_id,
            transactions(amount)
        `)
        .eq('contract_id', contractId)
        .in('type', [...SERVICE_TYPES])
        .order('started_at', { ascending: true });
    if (error) throw error;

    return ((data ?? []) as unknown as RawMaintenanceRow[]).map((row) => {
        const transaction = Array.isArray(row.transactions) ? row.transactions[0] : row.transactions;
        return {
            id: row.id,
            type: row.type,
            description: row.description,
            started_at: row.started_at,
            ended_at: row.ended_at,
            transaction_id: row.transaction_id,
            amount: transaction?.amount ?? null,
        };
    });
}

/** Create or amend one workshop record. */
export async function saveMaintenance({
    maintenanceId,
    vehicleId,
    contractId,
    draft,
}: {
    /** The row to amend; null creates one. */
    maintenanceId: number | null;
    vehicleId: number;
    contractId: number;
    draft: ServiceDraft;
}): Promise<void> {
    const refusal = validateServiceDraft(draft);
    if (refusal) throw new Error(refusal);

    const row = {
        type: draft.type as Database['public']['Enums']['maintenance_type'],
        description: draft.description.trim(),
        started_at: draft.startedAt,
        ended_at: draft.endedAt || null,
        transaction_id: Number(draft.transactionId),
        contract_id: contractId,
    };

    if (maintenanceId != null) {
        const { error } = await supabase
            .from('vehicle_maintenances')
            .update(row)
            .eq('id', maintenanceId);
        if (error) throw error;
        return;
    }

    const { error } = await supabase
        .from('vehicle_maintenances')
        .insert({ ...row, vehicle_id: vehicleId });
    if (error) throw error;
}
